import './AccountPages.scss'
import React, { useEffect, useState, useRef } from 'react'
import { useScript } from 'use-hooks'
import { useConnect } from 'redux-bundler-hook'
import BEMHelper from 'react-bem-helper'
import { get } from 'lodash'
import { navigate } from 'gatsby'
import useTranslations from '@src/hooks/useTranslations'
import FormContainer from '@src/components/FormContainer'
import CreditCardIFrameField from '@src/components/account/CreditCardIFrameField'
import Button from '@material-ui/core/Button'
import CircularProgress from '@material-ui/core/CircularProgress'
import {
	azureApiGatewayPost,
	remoteLogError,
} from '@src/api/azureApiGateway/client'
import { googleAnalytics } from '@src/api/googleanalytics/googleAnalytics'
import { REGISTRATION_STEPS } from '@src/components/account/Account'
import { localizedUrl } from '@src/utils/navigate'

const bem = new BEMHelper('registration-page')

function AddPaymentMethod({ setNextStep, setError }) {
	const t = useTranslations()
	const {
		customerInfo: { customerId = null },
		doRefreshCustomerInfo,
		doResetCustomerCreditCards,
		returnToBookingAfterRegistration,
		language,
		service,
	} = useConnect(
		'selectCustomerInfo',
		'doRefreshCustomerInfo',
		'doResetCustomerCreditCards',
		'selectReturnToBookingAfterRegistration',
		'selectLanguage',
		'selectService',
	)
	const [showBankModal, setShowBankModal] = useState(false)
	const [localLoading, setLocalLoading] = useState(true)
	const [addingCard, setAddingCard] = useState(0)

	const hostedFieldsInstance = useRef(null)
	const threeDSecure = useRef(null)

	const [numberValid, setNumberValid] = useState(false)
	const [expirationDateValid, setExpirationDateValid] = useState(false)
	const [cvvValid, setCvvValid] = useState(false)

	const [loadedUrl, loadUrlError] = useScript(
		'https://js.braintreegateway.com/web/3.61.0/js/client.min.js',
	)
	const [loadedUrl1, loadUrlError1] = useScript(
		'https://js.braintreegateway.com/web/3.61.0/js/hosted-fields.min.js',
	)
	const [loadedUrl2, loadUrlError2] = useScript(
		'https://js.braintreegateway.com/web/3.61.0/js/three-d-secure.min.js',
	)

	const getClientToken = async () => {
		try {
			const resp = await azureApiGatewayPost(
				`customer/${customerId}/card/initialization_token`,
				null,
				{ useToken: true, params: { registration: true } },
			)
			const braintreeClient = await window.braintree.client.create({
				authorization: resp.data.initialization_token,
			})

			hostedFieldsInstance.current = await window.braintree.hostedFields.create(
				{
					client: braintreeClient,
					styles: {
						body: {
							'background-color': '#fff',
						},
						input: {
							'font-size': '1rem',
							color: '#3A3A3A',
							transition: 'color 160ms linear',
							'-webkit-transition': 'color 160ms linear',
						},
					},
					fields: {
						number: {
							selector: '#hf-number',
							placeholder: t('kinto.registration.cardnumber'),
						},
						cvv: {
							selector: '#hf-cvv',
							placeholder: t('kinto.registration.cvc'),
						},
						expirationDate: {
							selector: '#hf-date',
							placeholder: t('kinto.registration.card-expire'),
						},
					},
				},
			)
			hostedFieldsInstance.current.on('validityChange', (event) => {
				setNumberValid(
					event.fields['number'].isValid ||
						event.fields['number'].isPotentiallyValid,
				)
				setExpirationDateValid(event.fields['expirationDate'].isValid)
				setCvvValid(event.fields['cvv'].isValid)
			})

			threeDSecure.current = await window.braintree.threeDSecure.create({
				client: braintreeClient,
				version: '2-inline-iframe',
			})

			threeDSecure.current.on('lookup-complete', (data, next) => {
				next()
			})
			threeDSecure.current.on(
				'authentication-iframe-available',
				(payload, next) => {
					setupBankFrame(payload.element)
					next()
				},
			)
		} catch (error) {
			setError({
				message: get(error, 'response.data.messages')
					? error.response.data.messages
					: 'kinto.error.init-card',
			})
		}
		setLocalLoading(false)
	}

	const setupBankFrame = (iframe) => {
		var bankModalWrapper = document.getElementById('bank-modal-wrapper')
		bankModalWrapper.innerHTML = ''
		bankModalWrapper.appendChild(iframe)
		bankModalWrapper.style.display = 'block'
		setShowBankModal(true)
	}

	const hideBankModal = () => {
		setShowBankModal(false)
		var bankModalWrapper = document.getElementById('bank-modal-wrapper')
		bankModalWrapper.innerHTML = ''
		bankModalWrapper.style.display = 'none'
	}

	useEffect(() => {
		googleAnalytics.pushVirtualPageView(
			`/virtual/account/addpaymentmethod/${customerId}`,
			`User with userid ${customerId} is adding payment method`,
		)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	useEffect(() => {
		if (
			loadedUrl &&
			loadedUrl1 &&
			loadedUrl2 &&
			!loadUrlError &&
			!loadUrlError1 &&
			!loadUrlError2
		) {
			getClientToken()
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [loadedUrl, loadedUrl1, loadedUrl2])

	const addCard = async () => {
		setLocalLoading(true)
		hideBankModal()
		try {
			hostedFieldsInstance.current.tokenize(async (tokenizeErr, payload) => {
				if (tokenizeErr) {
					setError({ message: 'kinto.error.card-not-valid' })
					return
				}

				if (addingCard > 0) {
					const n = addingCard + 1
					remoteLogError(
						`Adding card with nonce ${payload.nonce} for ${n}th time`,
					)
					return
				}
				setAddingCard(addingCard + 1)

				let data
				try {
					data = await threeDSecure.current.verifyCard({
						nonce: payload.nonce,
						bin: payload.binData,
						amount: '1.00',
					})
					remoteLogError(
						`Successfully added a credit card for customer with id ${customerId}`,
						'Info',
					)
				} catch (error) {
					setLocalLoading(false)
					setAddingCard(0)
					hideBankModal()
					const errorMessage = JSON.stringify(
						get(error, 'details.originalError'),
					)
					remoteLogError(
						`Failed to add a credit card for customer with id ${customerId}: ${errorMessage}`,
						'Error',
					)
					setError({
						message: 'kinto.error.verify-card-failed',
					})
					return
				}

				try {
					await azureApiGatewayPost(
						`customer/${customerId}/card`,
						{
							payment_card_token: data.nonce,
							in_use: true,
							card_tag: 'personal',
						},
						{ useToken: true, params: { registration: true } },
					)
					await doRefreshCustomerInfo()
					setLocalLoading(false)
					setAddingCard(0)
					if (returnToBookingAfterRegistration === true) {
						navigate(localizedUrl('/boka-bil', language, service))
					} else {
						setNextStep(REGISTRATION_STEPS.SUCCESS_PAGE)
					}
				} catch (error) {
					setLocalLoading(false)
					setAddingCard(0)
					hideBankModal()
					setError({
						message: get(error, 'response.data.messages')
							? error.response.data.messages
							: 'kinto.error.send-card-nonce-failed',
					})
				}
			})
		} catch (error) {
			setLocalLoading(false)
			setAddingCard(0)
			hideBankModal()
			setError({
				message: get(error, 'response.data.messages')
					? error.response.data.messages
					: 'kinto.error.init-send-card-nonce-failed',
			})
		}
	}
	return (
		<div {...bem('')}>
			<FormContainer>
				<CreditCardIFrameField id='hf-number' />
				<CreditCardIFrameField id='hf-date' />
				<CreditCardIFrameField id='hf-cvv' />
				<Button
					aria-label={t('kinto.general.next')}
					variant='contained'
					color='primary'
					onClick={addCard}
					disabled={
						!numberValid || !expirationDateValid || !cvvValid || localLoading
					}
				>
					{t('kinto.general.next')}
				</Button>
				<Button
					aria-label={t('kinto.general.later')}
					variant='outlined'
					color='primary'
					onClick={() => {
						doResetCustomerCreditCards()
						setNextStep(REGISTRATION_STEPS.SUCCESS_PAGE)
					}}
				>
					{t('kinto.general.later')}
				</Button>
			</FormContainer>
			<div
				{...bem('3ds-container')}
				style={{
					display: showBankModal ? 'flex' : 'none',
				}}
			>
				<div {...bem('3ds-container-overlay')} />
				<div id='bank-modal-wrapper'></div>
			</div>
			{localLoading && (
				<div {...bem('local-loading')}>
					<CircularProgress size='15rem' />
				</div>
			)}
		</div>
	)
}

export default AddPaymentMethod
